import { useCohostsList, useRoom } from ".";
import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import type { ApiError } from "@/react/config/CustomErrors";
import type { Cohost } from "@/react/types/Live/Cohost";
import { liveStreamApi } from "@circle-react/api";
import type { AddCohostsPayload } from "@circle-react/api/liveStreamApi";
import { useRemoveCommunityMemberAsCohost } from "@circle-react/components/LiveStreams/Room/hooks";
import {
  useProviderPeersList,
  useProviderRoleChange,
} from "@circle-react/components/LiveStreams/Room/hooks/provider";
import { useLiveStreamContextData } from "@circle-react/contexts/LiveStreams";
import { PARTICIPANT_ROLES } from "@circle-react/helpers/liveStreamRoomHelpers";
import { useToast } from "@circle-react-uikit/ToastV2";

export const useCohosts = (onAddCohosts?: () => void) => {
  const { success, error } = useToast();
  const room = useRoom();
  const { requestRoleChange } = useProviderRoleChange();
  const { spectators } = useProviderPeersList();
  const { participants } = useLiveStreamContextData();
  const { cohosts, isLoadingCohosts, refetchCohosts } = useCohostsList();

  const findActivePeerIdByCommunityMemberId = (communityMemberId: number) => {
    if (!spectators?.length) {
      return null;
    }
    const activePeer = spectators.find(
      peer =>
        participants?.[Number(peer.customerUserId) || 0]
          ?.community_member_id === communityMemberId,
    );
    return activePeer?.id;
  };

  const inviteActivePeersToCohost = (communityMembers: Cohost[]) => {
    communityMembers?.forEach(({ id: communityMemberId }) => {
      const peerId = findActivePeerIdByCommunityMemberId(communityMemberId);
      if (peerId) {
        // Invite through 100ms so that they can then accept
        // without reloading or leaving the session
        void requestRoleChange(peerId, PARTICIPANT_ROLES.HOST);
      }
    });
  };

  const addCohostsMutation = useMutation<Cohost[], ApiError, AddCohostsPayload>(
    formData =>
      liveStreamApi.addCohosts({
        roomId: room?.id,
        formData,
      }),
    {
      onSuccess: communityMembers => {
        inviteActivePeersToCohost(communityMembers);
        refetchCohosts();
        success(t("live_streams.room.cohosts_added_successfully"));
        onAddCohosts && onAddCohosts();
      },
      onError: () => error(t("live_streams.room.could_not_add_cohosts_error")),
    },
  );
  const removeCohost = useRemoveCommunityMemberAsCohost(refetchCohosts);
  const canInviteMoreHosts =
    room?.host_capacity && room.host_capacity > cohosts.length + 1;

  return {
    cohosts,
    isLoadingCohosts,
    addCohosts: addCohostsMutation.mutateAsync,
    isAddingCohosts: addCohostsMutation.isLoading,
    removeCohost,
    canInviteMoreHosts,
  };
};
